import { getPresetByKey } from '@engine/scale';
import { useTuningStore, type TuningSet } from './tuningStore';

/**
 * Shareable URL encoding for a tuning configuration.
 *
 * The goal: a teacher can paste a link into chat, the student opens it,
 * and the app loads with the exact preset + root + register + string
 * count + degree rotation they need — no manual setup.
 *
 * Format (query string on the app URL):
 *   ?preset=pentatonic-major   preset key from TUNING_PRESETS
 *   &root=C                    bass pitch class (C, C#, D, D#, …, B)
 *   &octave=3                  bass octave (1–8)
 *   &lowest=2                  lowestDegreeIdx (0..scaleSize-1)
 *   &strings=17                string count (5–25)
 *   &period=1200               scale period in cents (300–6000)
 *   &cents=0                   bass cents offset (-50..+50)
 *   &scale=0,200,400,700,900   custom scale (ABSOLUTE cents from root,
 *                              comma-separated). Overrides `preset`'s
 *                              scaleNotes AND implicitly sets scaleSize.
 *
 * Combined with `#tuner` in the hash the link opens straight into the
 * tuner view with the tuning pre-loaded — one URL gets the student
 * from "unopened tab" to "verifying each string" in three taps.
 */

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export interface TuningUrlConfig {
  presetKey?: string;
  bassPitchClass?: string;
  bassOctave?: number;
  bassCents?: number;
  lowestDegreeIdx?: number;
  stringCount?: number;
  scalePeriod?: number;
  /** Custom scale notes as absolute cents from root. Takes precedence
   *  over `presetKey` — the preset (if any) is applied first, then
   *  this replaces scaleNotes + scaleSize. */
  scaleCents?: number[];
}

/** Pull a tuning config out of the current URL. Returns null if no
 *  tuning params are present (caller should leave defaults untouched). */
export function parseTuningFromUrl(search: string = window.location.search):
  | TuningUrlConfig
  | null {
  const params = new URLSearchParams(search);
  const any =
    params.has('preset') ||
    params.has('root') ||
    params.has('octave') ||
    params.has('lowest') ||
    params.has('strings') ||
    params.has('period') ||
    params.has('cents') ||
    params.has('scale');
  if (!any) return null;

  const cfg: TuningUrlConfig = {};

  const preset = params.get('preset');
  if (preset && getPresetByKey(preset)) cfg.presetKey = preset;

  const root = params.get('root');
  if (root) {
    const normalized = root
      .replace('b', '#') // sharp-only model — silently drop flats
      .toUpperCase();
    if (NOTE_NAMES.includes(normalized)) cfg.bassPitchClass = normalized;
  }

  const octave = params.get('octave');
  if (octave !== null) {
    const n = parseInt(octave, 10);
    if (!Number.isNaN(n) && n >= 1 && n <= 8) cfg.bassOctave = n;
  }

  const cents = params.get('cents');
  if (cents !== null) {
    const n = parseFloat(cents);
    if (!Number.isNaN(n) && n >= -50 && n <= 50) cfg.bassCents = n;
  }

  const lowest = params.get('lowest');
  if (lowest !== null) {
    const n = parseInt(lowest, 10);
    if (!Number.isNaN(n) && n >= 0 && n <= 24) cfg.lowestDegreeIdx = n;
  }

  const strings = params.get('strings');
  if (strings !== null) {
    const n = parseInt(strings, 10);
    if (!Number.isNaN(n) && n >= 5 && n <= 25) cfg.stringCount = n;
  }

  const period = params.get('period');
  if (period !== null) {
    const n = parseInt(period, 10);
    if (!Number.isNaN(n) && n >= 300 && n <= 6000) cfg.scalePeriod = n;
  }

  const scale = params.get('scale');
  if (scale) {
    const parts = scale.split(',').map((s) => parseFloat(s));
    if (parts.length >= 1 && parts.every((x) => !Number.isNaN(x))) {
      cfg.scaleCents = parts;
    }
  }

  return cfg;
}

/** Build a shareable URL with the current tuning encoded. `baseUrl`
 *  defaults to the current page, and `includeTuner` appends `#tuner`
 *  when the sharer wants the link to open straight into tuning mode. */
export function buildTuningUrl(
  set: TuningSet,
  opts: { baseUrl?: string; includeTuner?: boolean } = {},
): string {
  const url = new URL(opts.baseUrl ?? window.location.href);
  // Drop any existing tuning params so the output is clean.
  const DROP = ['preset', 'root', 'octave', 'cents', 'lowest', 'strings', 'period', 'scale'];
  for (const k of DROP) url.searchParams.delete(k);

  if (set.presetKey) url.searchParams.set('preset', set.presetKey);
  url.searchParams.set('root', set.bassPitchClass);
  url.searchParams.set('octave', String(set.bassOctave));
  if (set.bassCents !== 0) url.searchParams.set('cents', String(set.bassCents));
  if ((set.lowestDegreeIdx ?? 0) !== 0) {
    url.searchParams.set('lowest', String(set.lowestDegreeIdx));
  }
  url.searchParams.set('strings', String(set.stringCount));
  if (set.scalePeriod !== 1200) {
    url.searchParams.set('period', String(set.scalePeriod));
  }
  // Include explicit scale notes only when the preset is null (a
  // custom tuning) — otherwise the preset key is enough.
  if (!set.presetKey) {
    const cents = set.scaleNotes.map((n) => Math.round(n.semitones * 100 + n.cents));
    url.searchParams.set('scale', cents.join(','));
  }

  url.hash = opts.includeTuner ? '#tuner' : '';
  return url.toString();
}

/** Apply a parsed URL config to the FIRST set in the tuning store.
 *  Preset (if specified) is applied first via `applyPreset` so the
 *  scaleNotes / scaleSize / lowestDegreeIdx get their canonical
 *  starting values; the remaining fields then overlay on top. Scale
 *  cents (explicit custom scale) fully replaces scaleNotes last. */
export function applyUrlConfigToStore(cfg: TuningUrlConfig): void {
  const store = useTuningStore.getState();
  const targetIdx = 0;

  if (cfg.presetKey) {
    store.applyPreset(targetIdx, cfg.presetKey);
  }

  // Compute overlay patch from the remaining fields.
  const patch: Partial<TuningSet> = {};
  if (cfg.bassPitchClass) patch.bassPitchClass = cfg.bassPitchClass;
  if (cfg.bassOctave !== undefined) patch.bassOctave = cfg.bassOctave;
  if (cfg.bassCents !== undefined) patch.bassCents = cfg.bassCents;
  if (cfg.lowestDegreeIdx !== undefined) patch.lowestDegreeIdx = cfg.lowestDegreeIdx;
  if (cfg.stringCount !== undefined) patch.stringCount = cfg.stringCount;
  if (cfg.scalePeriod !== undefined) patch.scalePeriod = cfg.scalePeriod;

  if (cfg.scaleCents && cfg.scaleCents.length > 0) {
    patch.scaleNotes = cfg.scaleCents.map((totalCents) => {
      const semi = Math.floor(totalCents / 100);
      const cents = totalCents - semi * 100;
      return { semitones: semi, cents };
    });
    patch.scaleSize = cfg.scaleCents.length;
    // Custom scale forfeits the preset label.
    patch.presetKey = null;
  }

  if (Object.keys(patch).length > 0) {
    store.updateSet(targetIdx, patch);
  }
}
